import React, { useState, useEffect } from 'react';
import api from '../../utils/api';
import { toast } from 'react-toastify';

const PlatformSettings = () => {
  const [settings, setSettings] = useState({
    platformName: 'VestrioZ',
    platformDescription: 'Premium Fashion Rental Platform',
    maintenanceMode: false,
    registrationEnabled: true,
    emailNotifications: true,
    smsNotifications: true,
    maxListingImages: 1,
    defaultListingDuration: 30,
    platformCommission: 10,
    minRentalPrice: 50,
    maxRentalPrice: 10000
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      // This would be actual API call when implemented
      // const response = await api.get('/admin/settings');
      // setSettings(response.data.settings || settings);
    } catch (error) {
      toast.error('Error loading platform settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      // This would be actual API call when implemented
      // await api.put('/admin/settings', settings);
      toast.success('Platform settings updated successfully');
    } catch (error) {
      toast.error('Error saving platform settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-t-4 rounded-full animate-spin mx-auto mb-4" 
               style={{ borderColor: '#DFD0B8', borderTopColor: '#948979' }}></div>
          <p className="font-medium" style={{ color: '#393E46' }}>Loading platform settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="space-y-6 lg:space-y-8">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 lg:mb-8">
        <div>
          <div className="flex items-center space-x-3 mb-2">
            <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831' }}>
              <i className="fas fa-cog text-white text-lg lg:text-xl"></i>
            </div>
            <h2 className="text-2xl lg:text-3xl font-bold" style={{ color: '#222831' }}>
              Platform Settings
            </h2>
          </div>
          <p className="ml-13 lg:ml-15 text-sm lg:text-base" style={{ color: '#393E46' }}>Configure platform-wide settings and preferences</p>
        </div>
        <button
          onClick={saveSettings}
          disabled={saving}
          className="mt-4 lg:mt-0 px-6 lg:px-8 py-3 rounded-2xl text-white font-bold shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 text-sm lg:text-base"
          style={{ backgroundColor: '#948979' }}
        >
          {saving ? (
            <>
              <i className="fas fa-spinner fa-spin mr-2"></i>
              Saving...
            </>
          ) : (
            <>
              <i className="fas fa-save mr-2"></i>
              Save Settings
            </>
          )}
        </button>
      </div>

      {/* General Settings */}
      <div className="bg-white rounded-3xl shadow-xl p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#948979' }}>
            <i className="fas fa-info-circle text-white text-lg"></i>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: '#222831' }}>General Settings</h3>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
          <div>
            <label className="block text-sm font-bold mb-3" style={{ color: '#393E46' }}>Platform Name</label>
            <input
              type="text"
              value={settings.platformName}
              onChange={(e) => handleInputChange('platformName', e.target.value)}
              className="w-full px-4 py-3 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 font-medium text-sm lg:text-base"
              style={{ 
                borderColor: '#DFD0B8', 
                backgroundColor: '#DFD0B8',
                color: '#222831'
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-3" style={{ color: '#393E46' }}>Platform Description</label>
            <input
              type="text"
              value={settings.platformDescription}
              onChange={(e) => handleInputChange('platformDescription', e.target.value)}
              className="w-full px-4 py-3 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 font-medium text-sm lg:text-base"
              style={{ 
                borderColor: '#DFD0B8', 
                backgroundColor: '#DFD0B8',
                color: '#222831'
              }}
            />
          </div>
        </div>
      </div>

      {/* System Settings */}
      <div className="bg-white rounded-3xl shadow-xl p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#393E46' }}>
            <i className="fas fa-server text-white text-lg"></i>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: '#222831' }}>System Settings</h3>
        </div>
        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 rounded-2xl border-2" style={{ borderColor: '#DFD0B8' }}>
            <div>
              <h4 className="font-bold mb-1" style={{ color: '#222831' }}>Maintenance Mode</h4>
              <p className="text-sm" style={{ color: '#393E46' }}>Enable to temporarily disable platform access</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.maintenanceMode}
                onChange={(e) => handleInputChange('maintenanceMode', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 rounded-2xl border-2" style={{ borderColor: '#DFD0B8' }}>
            <div>
              <h4 className="font-bold mb-1" style={{ color: '#222831' }}>User Registration</h4>
              <p className="text-sm" style={{ color: '#393E46' }}>Allow new users to register on the platform</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.registrationEnabled}
                onChange={(e) => handleInputChange('registrationEnabled', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white rounded-3xl shadow-xl p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#948979' }}>
            <i className="fas fa-bell text-white text-lg"></i>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: '#222831' }}>Notification Settings</h3>
        </div>
        <div className="space-y-6">
          <div className="flex items-center justify-between p-4 rounded-2xl border-2" style={{ borderColor: '#DFD0B8' }}>
            <div>
              <h4 className="font-bold mb-1" style={{ color: '#222831' }}>Email Notifications</h4>
              <p className="text-sm" style={{ color: '#393E46' }}>Send email notifications to users</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.emailNotifications}
                onChange={(e) => handleInputChange('emailNotifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-4 rounded-2xl border-2" style={{ borderColor: '#DFD0B8' }}>
            <div>
              <h4 className="font-bold mb-1" style={{ color: '#222831' }}>SMS Notifications</h4>
              <p className="text-sm" style={{ color: '#393E46' }}>Send SMS notifications to users</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={settings.smsNotifications}
                onChange={(e) => handleInputChange('smsNotifications', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Business Settings */}
      <div className="bg-white rounded-3xl shadow-xl p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#222831' }}>
            <i className="fas fa-business-time text-white text-lg"></i>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: '#222831' }}>Business Settings</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          <div>
            <label className="block text-sm font-bold mb-3" style={{ color: '#393E46' }}>Max Listing Images</label>
            <input
              type="number"
              min="1"
              max="10"
              value={settings.maxListingImages}
              onChange={(e) => handleInputChange('maxListingImages', parseInt(e.target.value))}
              className="w-full px-4 py-3 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 font-medium text-sm lg:text-base"
              style={{ 
                borderColor: '#DFD0B8', 
                backgroundColor: '#DFD0B8',
                color: '#222831'
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-3" style={{ color: '#393E46' }}>Default Listing Duration (days)</label>
            <input
              type="number"
              min="1"
              max="365"
              value={settings.defaultListingDuration}
              onChange={(e) => handleInputChange('defaultListingDuration', parseInt(e.target.value))}
              className="w-full px-4 py-3 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 font-medium text-sm lg:text-base"
              style={{ 
                borderColor: '#DFD0B8', 
                backgroundColor: '#DFD0B8',
                color: '#222831'
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-3" style={{ color: '#393E46' }}>Platform Commission (%)</label>
            <input
              type="number"
              min="0"
              max="50"
              value={settings.platformCommission}
              onChange={(e) => handleInputChange('platformCommission', parseInt(e.target.value))}
              className="w-full px-4 py-3 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 font-medium text-sm lg:text-base"
              style={{ 
                borderColor: '#DFD0B8', 
                backgroundColor: '#DFD0B8',
                color: '#222831'
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-3" style={{ color: '#393E46' }}>Min Rental Price (₹)</label>
            <input
              type="number"
              min="1"
              value={settings.minRentalPrice}
              onChange={(e) => handleInputChange('minRentalPrice', parseInt(e.target.value))}
              className="w-full px-4 py-3 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 font-medium text-sm lg:text-base"
              style={{ 
                borderColor: '#DFD0B8', 
                backgroundColor: '#DFD0B8',
                color: '#222831'
              }}
            />
          </div>
          <div>
            <label className="block text-sm font-bold mb-3" style={{ color: '#393E46' }}>Max Rental Price (₹)</label>
            <input
              type="number"
              min="100"
              value={settings.maxRentalPrice}
              onChange={(e) => handleInputChange('maxRentalPrice', parseInt(e.target.value))}
              className="w-full px-4 py-3 border-2 rounded-2xl focus:ring-2 focus:border-transparent transition-all duration-200 font-medium text-sm lg:text-base"
              style={{ 
                borderColor: '#DFD0B8', 
                backgroundColor: '#DFD0B8',
                color: '#222831'
              }}
            />
          </div>
        </div>
      </div>

      {/* System Information */}
      <div className="bg-white rounded-3xl shadow-xl p-8 border-2" style={{ borderColor: '#DFD0B8' }}>
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-2xl flex items-center justify-center shadow-lg" style={{ backgroundColor: '#393E46' }}>
            <i className="fas fa-info text-white text-lg"></i>
          </div>
          <h3 className="text-2xl font-bold" style={{ color: '#222831' }}>System Information</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          <div className="text-center p-4 lg:p-6 rounded-2xl border-2" style={{ borderColor: '#DFD0B8', backgroundColor: '#DFD0B8' }}>
            <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 lg:mb-4" style={{ backgroundColor: '#948979' }}>
              <i className="fas fa-server text-white text-lg lg:text-2xl"></i>
            </div>
            <h4 className="font-bold mb-2 text-sm lg:text-base" style={{ color: '#222831' }}>Server Status</h4>
            <p className="text-xs lg:text-sm font-medium text-green-600">Online</p>
          </div>
          <div className="text-center p-4 lg:p-6 rounded-2xl border-2" style={{ borderColor: '#DFD0B8', backgroundColor: '#DFD0B8' }}>
            <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 lg:mb-4" style={{ backgroundColor: '#222831' }}>
              <i className="fas fa-database text-white text-lg lg:text-2xl"></i>
            </div>
            <h4 className="font-bold mb-2 text-sm lg:text-base" style={{ color: '#222831' }}>Database</h4>
            <p className="text-xs lg:text-sm font-medium text-green-600">Connected</p>
          </div>
          <div className="text-center p-4 lg:p-6 rounded-2xl border-2" style={{ borderColor: '#DFD0B8', backgroundColor: '#DFD0B8' }}>
            <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-2xl flex items-center justify-center mx-auto mb-3 lg:mb-4" style={{ backgroundColor: '#948979' }}>
              <i className="fas fa-code-branch text-white text-lg lg:text-2xl"></i>
            </div>
            <h4 className="font-bold mb-2 text-sm lg:text-base" style={{ color: '#222831' }}>Version</h4>
            <p className="text-xs lg:text-sm font-medium" style={{ color: '#393E46' }}>v1.0.0</p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default PlatformSettings;